import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/app/store';
import { ACCENT_COLORS, DEFAULT_ACCENT } from '@/domain/types';
import { nowIso } from '@/domain/helpers';
import { profileRepo, spaceRepo, preferencesRepo } from '@/repositories';
import { setMeta, DEFAULT_BACKGROUND } from '@/storage/db';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { classifyJoinError } from '@/domain/spaceErrors';
import styles from './OnboardingFlow.module.css';

type Step = 'profile' | 'space' | 'invite' | 'tour';

export function OnboardingFlow() {
  const { t, i18n } = useTranslation();
  const session = useAppStore((s) => s.session);
  const space = useAppStore((s) => s.space);
  const profiles = useAppStore((s) => s.profiles);
  const preferences = useAppStore((s) => s.preferences);
  const spaceReady = useAppStore((s) => s.spaceReady);
  const refreshSpaceData = useAppStore((s) => s.refreshSpaceData);
  const updatePreferences = useAppStore((s) => s.updatePreferences);

  const me = profiles.find((p) => p.id === session?.user.id);
  const [step, setStep] = useState<Step | null>(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(DEFAULT_ACCENT);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [joinCode, setJoinCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Partner path is default so "Join" does not accidentally create a solo space first.
  const [spaceMode, setSpaceMode] = useState<'create' | 'join'>('join');

  useEffect(() => {
    if (!session || !spaceReady) {
      setStep(null);
      return;
    }
    if (!me || !me.displayName.trim()) {
      setName(me?.displayName ?? '');
      setColor(me?.accentColor ?? DEFAULT_ACCENT);
      setStep('profile');
      return;
    }
    if (!space) {
      setStep('space');
      return;
    }
    if (profiles.length >= 2 && preferences && !preferences.onboardingCompleted) {
      setStep('tour');
      return;
    }
    setStep((current) => (current === 'invite' ? current : null));
  }, [session, me, space, profiles.length, preferences, spaceReady]);

  if (!session || step === null) return null;

  const mapJoinUiError = (err: unknown): string => {
    const msg = err instanceof Error ? err.message : String(err ?? '');
    switch (classifyJoinError(msg)) {
      case 'expired':
        return t('app.inviteExpired');
      case 'full':
        return t('app.spaceFull');
      case 'already_in_space':
        return t('auth.errors.alreadyMember');
      case 'cannot_leave':
        return t('auth.errors.cannotLeaveForInvite');
      case 'invalid':
        return t('auth.errors.invalidInvite');
      default:
        return msg.trim() || t('errors.generic');
    }
  };

  const saveProfile = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const ts = nowIso();
      const locale = i18n.language.startsWith('ru') ? 'ru' : 'en';
      await profileRepo.upsert({
        id: session.user.id,
        displayName: name.trim(),
        avatarPath: null,
        accentColor: color,
        locale,
        createdAt: me?.createdAt ?? ts,
        updatedAt: ts,
      });
      await preferencesRepo.upsert({
        userId: session.user.id,
        timeFormat: preferences?.timeFormat ?? (locale === 'ru' ? '24h' : '12h'),
        weekStartsOn: preferences?.weekStartsOn ?? (locale === 'ru' ? 1 : 0),
        notifications: preferences?.notifications ?? { enabled: false, offsets: [] },
        background: preferences?.background ?? { ...DEFAULT_BACKGROUND },
        onboardingCompleted: preferences?.onboardingCompleted ?? false,
        shortcutsHintSeen: preferences?.shortcutsHintSeen ?? false,
        locale,
        updatedAt: ts,
      });
      await refreshSpaceData();
    } finally {
      setBusy(false);
    }
  };

  const createSpace = async () => {
    setBusy(true);
    setError(null);
    try {
      const { inviteCode: code } = await spaceRepo.createSpace(session.user.id);
      await setMeta('last_invite_code', code);
      setInviteCode(code);
      await refreshSpaceData();
      setStep('invite');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const joinSpace = async () => {
    if (!joinCode.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await spaceRepo.joinByCode(session.user.id, joinCode.trim());
      await refreshSpaceData();
      setStep(null);
    } catch (err) {
      setError(mapJoinUiError(err));
    } finally {
      setBusy(false);
    }
  };

  const finishTour = async () => {
    await updatePreferences({ onboardingCompleted: true });
    setStep(null);
  };

  const title =
    step === 'profile'
      ? t('onboarding.chooseName')
      : step === 'space'
        ? spaceMode === 'join'
          ? t('onboarding.joinTitle')
          : t('onboarding.createSpace')
        : step === 'invite'
          ? t('onboarding.inviteTitle')
          : t('onboarding.productTourTitle');

  return (
    <Modal open title={title} onClose={() => {
      if (step === 'invite' || step === 'tour') setStep(null);
    }}>
      {step === 'profile' && (
        <div className={styles.body}>
          <Input
            label={t('settings.displayName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <div className={styles.colors} role="listbox" aria-label={t('settings.accentColor')}>
            {ACCENT_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={styles.swatch}
                style={{ background: c }}
                aria-pressed={color === c}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
          <Button variant="primary" loading={busy} onClick={() => void saveProfile()}>
            {t('common.next')}
          </Button>
        </div>
      )}

      {step === 'space' && (
        <div className={styles.body}>
          <div className={styles.actions}>
            <Button
              variant={spaceMode === 'join' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => {
                setSpaceMode('join');
                setError(null);
              }}
            >
              {t('onboarding.joinTitle')}
            </Button>
            <Button
              variant={spaceMode === 'create' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => {
                setSpaceMode('create');
                setError(null);
              }}
            >
              {t('onboarding.createSpace')}
            </Button>
          </div>
          {spaceMode === 'create' ? (
            <>
              <p className={styles.hint}>{t('onboarding.inviteHint')}</p>
              {error && (
                <p className={styles.hint} role="alert">
                  {error}
                </p>
              )}
              <Button variant="primary" loading={busy} onClick={() => void createSpace()}>
                {t('onboarding.createSpace')}
              </Button>
            </>
          ) : (
            <>
              <p className={styles.hint}>{t('onboarding.joinHint')}</p>
              <Input
                label={t('onboarding.inviteCode')}
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              />
              {error && (
                <p className={styles.hint} role="alert">
                  {error}
                </p>
              )}
              <Button variant="primary" loading={busy} onClick={() => void joinSpace()}>
                {t('onboarding.joinConfirm')}
              </Button>
            </>
          )}
        </div>
      )}

      {step === 'invite' && (
        <div className={styles.body}>
          <p className={styles.hint}>{t('onboarding.inviteHint')}</p>
          <code className={styles.code}>{inviteCode ?? '------'}</code>
          <Button
            variant="secondary"
            onClick={() => {
              if (inviteCode) void navigator.clipboard.writeText(inviteCode);
            }}
          >
            {t('onboarding.copyInvite')}
          </Button>
          <Button variant="primary" onClick={() => setStep(null)}>
            {t('common.done')}
          </Button>
        </div>
      )}

      {step === 'tour' && (
        <div className={styles.body}>
          <p className={styles.hint}>{t('onboarding.productTourBody')}</p>
          <div className={styles.actions}>
            <Button variant="ghost" onClick={() => void finishTour()}>
              {t('onboarding.skipTour')}
            </Button>
            <Button variant="primary" onClick={() => void finishTour()}>
              {t('common.done')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
